/**
 * Vergütungsantrag (VBVG) – eigenständige Tab-Seite
 */
import { useState, useEffect } from 'react';
import { Calculator, Check, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { betreuungAPI } from '@/api/betreuungAPI';
import { useTabs } from '@/contexts/TabContext';
import { emitDataRefresh } from '@/lib/tabEvents';
import { toast } from 'sonner';

const BACKEND_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function VerguetungPage() {
  const { activeTab, closeTab } = useTabs();
  const params = activeTab?.params || {};
  const editData = params.editData;

  const [saving, setSaving] = useState(false);
  const [berechnung, setBerechnung] = useState(null);
  const [form, setForm] = useState({
    zeitraum_von: '', zeitraum_bis: '', betrag_netto: '', betrag_brutto: '',
    status: 'entwurf', antrag_datum: '', bewilligung_datum: '', auszahlung_datum: '', bemerkungen: ''
  });

  useEffect(() => {
    if (editData) {
      setForm({
        zeitraum_von: editData.zeitraum_von || '', zeitraum_bis: editData.zeitraum_bis || '',
        betrag_netto: editData.betrag_netto || '', betrag_brutto: editData.betrag_brutto || '',
        status: editData.status || 'entwurf', antrag_datum: editData.antrag_datum || '',
        bewilligung_datum: editData.bewilligung_datum || '', auszahlung_datum: editData.auszahlung_datum || '',
        bemerkungen: editData.bemerkungen || ''
      });
    } else {
      const now = new Date();
      const q = Math.floor(now.getMonth() / 3);
      const year = q === 0 ? now.getFullYear() - 1 : now.getFullYear();
      const qStart = q === 0 ? 4 : q;
      const startMonth = (qStart - 1) * 3;
      const endMonth = startMonth + 2;
      setForm({
        zeitraum_von: `${year}-${String(startMonth + 1).padStart(2, '0')}-01`,
        zeitraum_bis: `${year}-${String(endMonth + 1).padStart(2, '0')}-${[31,28,31,30,31,30,31,31,30,31,30,31][endMonth]}`,
        betrag_netto: '', betrag_brutto: '', status: 'entwurf',
        antrag_datum: now.toISOString().slice(0, 10), bewilligung_datum: '', auszahlung_datum: '', bemerkungen: ''
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBerechnen = async () => {
    if (!form.zeitraum_von || !form.zeitraum_bis) { toast.error('Bitte Zeitraum angeben'); return; }
    try {
      const res = await fetch(`${BACKEND_URL}/api/betreuung/verguetung/berechne`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ klient_id: params.klientId, zeitraum_von: form.zeitraum_von, zeitraum_bis: form.zeitraum_bis })
      }).then(r => r.json());
      if (res.gesamt_netto) {
        setBerechnung(res);
        setForm(p => ({ ...p, betrag_netto: res.gesamt_netto, betrag_brutto: res.gesamt_brutto }));
        toast.success(`Berechnet: ${res.gesamt_netto.toFixed(2)} € (${res.monatspauschalen?.length || 0} Monate)`);
      } else if (res.detail) {
        // z.B. 'Betreuungsbeginn klären: ...' -- ohne belegten Beginn wird keine Vergütung berechnet
        setBerechnung(null);
        toast.error(typeof res.detail === 'string' ? res.detail : 'Berechnung nicht möglich', { duration: 10000 });
      }
    } catch { toast.error('Fehler bei Berechnung'); }
  };

  const handleSave = async () => {
    if (!form.zeitraum_von || !form.zeitraum_bis) { toast.error('Bitte Zeitraum angeben'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form, klient_id: params.klientId,
        betrag_netto: form.betrag_netto ? parseFloat(form.betrag_netto) : null,
        betrag_brutto: form.betrag_brutto ? parseFloat(form.betrag_brutto) : null,
        monatspauschalen: berechnung?.monatspauschalen || null,
        aus_staatskasse: berechnung?.aus_staatskasse || false,
        frist_15_monate: berechnung?.frist_15_monate || null
      };
      if (editData) { await betreuungAPI.updateVerguetungsantrag(editData.id, payload); toast.success('Antrag aktualisiert'); }
      else { await betreuungAPI.createVerguetungsantrag(payload); toast.success('Antrag angelegt'); }
      emitDataRefresh('betreuungsakte');
      closeTab(activeTab.id);
    } catch { toast.error('Fehler beim Speichern'); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-lg mx-auto" data-testid="verguetung-page">
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Calculator className="w-5 h-5 text-green-600" />{editData ? 'Antrag bearbeiten' : 'Neuer Vergütungsantrag (VBVG)'}
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={() => closeTab(activeTab.id)}><X className="w-4 h-4" /></Button>
          </div>
          <CardDescription>{params.klientName || ''}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Zeitraum von</Label><Input type="date" value={form.zeitraum_von} onChange={e => setForm(p => ({ ...p, zeitraum_von: e.target.value }))} /></div>
            <div><Label className="text-xs">Zeitraum bis</Label><Input type="date" value={form.zeitraum_bis} onChange={e => setForm(p => ({ ...p, zeitraum_bis: e.target.value }))} /></div>
          </div>
          <Button type="button" variant="outline" className="w-full" onClick={handleBerechnen}>
            <Calculator className="w-4 h-4 mr-2" /> Nach VBVG berechnen
          </Button>
          {berechnung && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2 text-sm">
              <p className="font-medium text-blue-800">VBVG-Berechnung (ab 01.01.2026)</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <span>Stufe: <strong>{berechnung.stufe}</strong></span>
                <span>Wohnform: <strong>{berechnung.wohnform === 'stationaer' ? 'Stationär' : 'Sonstig'}</strong></span>
                <span>Mittellos: <strong>{berechnung.mittellos ? 'Ja' : 'Nein'}</strong></span>
                <span>Monate: <strong>{berechnung.monatspauschalen?.length || 0}</strong></span>
              </div>
              {berechnung.monatspauschalen?.length > 0 && (
                <div className="text-xs text-blue-700 max-h-24 overflow-y-auto">
                  {berechnung.monatspauschalen.map((m, i) => (
                    <div key={i} className="flex justify-between border-b border-blue-100 py-0.5">
                      <span>{m.monat} (Monat {m.betreuungsmonat})</span>
                      <span className="font-medium">{m.betrag.toFixed(2)} €</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex justify-between pt-2 border-t border-blue-200 font-medium">
                <span>Gesamt:</span><span className="text-blue-900">{berechnung.gesamt_netto?.toFixed(2)} €</span>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Betrag Netto (€)</Label><Input type="number" step="0.01" value={form.betrag_netto} onChange={e => setForm(p => ({ ...p, betrag_netto: e.target.value }))} /></div>
            <div><Label className="text-xs">Betrag Brutto (€)</Label><Input type="number" step="0.01" value={form.betrag_brutto} onChange={e => setForm(p => ({ ...p, betrag_brutto: e.target.value }))} /></div>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <Select value={form.status} onValueChange={v => setForm(p => ({ ...p, status: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="entwurf">Entwurf</SelectItem>
                <SelectItem value="gestellt">Gestellt</SelectItem>
                <SelectItem value="bewilligt">Bewilligt</SelectItem>
                <SelectItem value="ausgezahlt">Ausgezahlt</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs">Antrag am</Label><Input type="date" value={form.antrag_datum} onChange={e => setForm(p => ({ ...p, antrag_datum: e.target.value }))} /></div>
            <div><Label className="text-xs">Bewilligt am</Label><Input type="date" value={form.bewilligung_datum} onChange={e => setForm(p => ({ ...p, bewilligung_datum: e.target.value }))} /></div>
            <div><Label className="text-xs">Ausgezahlt am</Label><Input type="date" value={form.auszahlung_datum} onChange={e => setForm(p => ({ ...p, auszahlung_datum: e.target.value }))} /></div>
          </div>
          <div>
            <Label className="text-xs">Bemerkungen</Label>
            <Textarea value={form.bemerkungen} onChange={e => setForm(p => ({ ...p, bemerkungen: e.target.value }))} rows={2} />
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => closeTab(activeTab.id)}>Abbrechen</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-verguetung">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Speichern...</> : <><Check className="w-4 h-4 mr-2" />{editData ? 'Speichern' : 'Anlegen'}</>}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
